import { defineStore } from 'pinia';
import localStore from 'store';
import { computed, ref } from 'vue';

import { showFlash } from '@/alerts';
import { dashboardBffClient } from '@/api-client';
import type { BootstrapList, ClusterList, JoinTokenInfo, MCMetrics } from '@/api-client/api';
import constants from '@/constants';
import { error as logError } from '@/logger';
import { useAppInfoStore } from '@/stores/appInfo';

export const METRICS_DURATION = ['1h', '1d', '1w'] as const;
export type MetricsDuration = (typeof METRICS_DURATION)[number];

export const useMultiClusterStore = defineStore('multiCluster', () => {
  const clusters = ref<ClusterList | null>(null);
  const proxiedCluster = ref<string>();
  const bootstraps = ref<BootstrapList | null>(null);
  const metrics = ref<MCMetrics | null>(null);
  const clustersForbidden = ref(false);
  const joinTokens = ref<JoinTokenInfo[] | null>(null);

  const primaryCluster = computed(() => clusters.value?.find((c) => c.spec?.primary));

  async function fetchClusters() {
    try {
      const { data } = await dashboardBffClient.mc.listClusters(
        [],
        {},
        { shouldNotBeProxied: true },
      );
      const sortedClusters = data.sort((a, b) => {
        return (a.metadata?.name ?? '').localeCompare(b.metadata?.name ?? '');
      });
      const primary = sortedClusters.find((cluster) => cluster.spec?.primary);
      localStore.set(constants.localStorage.PRIMARY, primary?.metadata?.name);
      clusters.value = sortedClusters;
    } catch (err) {
      logError('Error fetching clusters', err);
      if (err.response.status === 403) {
        clustersForbidden.value = true;
        // if this is a primary cluster but the user does not have cluster list permissions
        // add a dummy cluster name to populate the proxy-select
        // so the user can navigate to the single cluster context
        const appInfoStore = useAppInfoStore();
        if (appInfoStore.useMCLayout) {
          clusters.value = [
            {
              metadata: { name: constants.MULTICLUSTER_DUMMY_PRIMARY_NAME },
              spec: { primary: true },
            },
          ];
        }
      }
    }
  }

  async function fetchBootstraps() {
    const { data } = await dashboardBffClient.mc.listBootstraps();
    // sort by date in descending order
    bootstraps.value = data.sort(
      (a, b) =>
        new Date(b.metadata?.creationTimestamp ?? '').getTime() -
        new Date(a.metadata?.creationTimestamp ?? '').getTime(),
    );
  }

  async function deleteBootstrap(name: string) {
    await dashboardBffClient.mc.deleteBootstrap(name);
    showFlash({
      message: 'Status deleted',
      details: 'The cluster status was successfully deleted.',
      messageType: 'success',
    });
    await fetchBootstraps();
  }

  async function fetchMetrics(duration: MetricsDuration) {
    const { data } = await dashboardBffClient.mc.mcMetrics({ duration });
    metrics.value = data;
  }

  async function fetchJoinTokens() {
    const { data } = await dashboardBffClient.mc.listJoinTokens();
    joinTokens.value = data;
  }

  async function createToken(name: string) {
    const { data } = await dashboardBffClient.mc.createJoinToken({ joinTokenName: name });
    return data;
  }

  async function deleteToken(name: string) {
    await dashboardBffClient.mc.deleteJoinToken(name);
  }

  return {
    clusters,
    proxiedCluster,
    bootstraps,
    metrics,
    clustersForbidden,
    joinTokens,
    primaryCluster,
    fetchClusters,
    fetchBootstraps,
    deleteBootstrap,
    fetchMetrics,
    fetchJoinTokens,
    createToken,
    deleteToken,
  };
});
